<template>
  <section>
    <p class="muted">
      Conecte armazenamento em nuvem via OAuth (sem colar tokens). Uploads podem rodar o OCR da
      fase 7. Google Keep permanece indisponível para contas pessoais. Gmail (leitura de e-mails) usa
      o mesmo Google Drive: clique em Reconectar para conceder permissão de e-mail.
    </p>
    <p class="flash ok" v-if="flashOk">{{ flashOk }}</p>
    <p class="flash err" v-if="flashErr">{{ flashErr }}</p>

    <h2 style="margin-top: 1.25rem">Provedores</h2>
    <div class="grid" style="margin-top: 0.75rem">
      <article class="panel" v-for="p in providers" :key="p.id">
        <h2>{{ p.name }}</h2>
        <p class="status" :class="statusClass(p.status)">{{ statusLabel(p.status) }}</p>
        <p class="muted" v-if="p.accountLabel">Conta: {{ p.accountLabel }}</p>
        <p class="muted" v-if="p.scopes?.length">Escopos: {{ p.scopes.slice(0, 4).join(', ') }}{{ p.scopes.length > 4 ? '…' : '' }}</p>
        <p class="muted" v-if="p.lastTestAt">Último teste: {{ formatDate(p.lastTestAt) }}</p>
        <p class="muted err-text" v-if="p.error">{{ p.error }}</p>
        <div class="actions" style="margin-top: 0.75rem">
          <button
            type="button"
            v-if="p.capabilities.oauth && p.status !== 'connected'"
            :disabled="busy === p.id"
            @click="connect(p.id)"
          >
            Conectar
          </button>
          <button
            type="button"
            v-if="p.capabilities.oauth && p.status === 'connected'"
            :disabled="busy === p.id"
            @click="connect(p.id)"
            title="Atualiza permissões (ex.: leitura do Gmail) sem perder Drive/Agenda"
          >
            Reconectar
          </button>
          <button
            type="button"
            class="secondary"
            v-if="p.capabilities.oauth && p.status === 'connected'"
            :disabled="busy === p.id"
            @click="disconnect(p.id)"
          >
            Desconectar
          </button>
          <button
            type="button"
            class="secondary"
            v-if="p.status !== 'unavailable'"
            :disabled="busy === p.id"
            @click="test(p.id)"
          >
            Testar
          </button>
        </div>
        <p class="muted" v-if="!appsReady(p.id) && p.capabilities.oauth">
          App OAuth ainda sem client id/secret no servidor.
        </p>
      </article>
    </div>

    <h2 style="margin-top: 1.75rem">Documentos</h2>
    <div class="panel form" style="margin-top: 0.75rem">
      <label>
        Destino do upload
        <select v-model="uploadProvider">
          <option value="local">Só local + OCR</option>
          <option value="google_drive">Google Drive</option>
          <option value="onedrive">OneDrive</option>
          <option value="dropbox">Dropbox</option>
        </select>
      </label>
      <label v-if="uploadProvider !== 'local'">
        Pasta (ID opcional)
        <input v-model="folderId" placeholder="raiz do provedor se vazio" />
      </label>
      <label class="check">
        <input v-model="runOcr" type="checkbox" />
        Processar com OCR / extrator (fase 7)
      </label>
      <div
        class="drop"
        :class="{ over: dragOver }"
        @dragover.prevent="dragOver = true"
        @dragleave.prevent="dragOver = false"
        @drop.prevent="onDrop"
      >
        <p>Arraste arquivos aqui ou</p>
        <label class="file-btn">
          Escolher arquivos
          <input type="file" multiple hidden @change="onPick" />
        </label>
      </div>
      <ul class="plain" v-if="uploads.length">
        <li v-for="u in uploads" :key="u.key">
          <strong>{{ u.name }}</strong>
          <span class="muted"> · {{ u.state }}</span>
          <span class="muted" v-if="u.detail"> — {{ u.detail }}</span>
        </li>
      </ul>
    </div>

    <div class="panel" style="margin-top: 1rem">
      <div class="actions" style="justify-content: space-between">
        <h2>Acervo local</h2>
        <form class="inline" @submit.prevent="loadDocs">
          <input v-model="docQuery" placeholder="buscar no texto…" />
          <button type="submit" :disabled="docsLoading">Buscar</button>
        </form>
      </div>
      <p class="muted" v-if="docsLoading">Carregando…</p>
      <ul class="plain" v-else>
        <li v-for="d in documents" :key="d.id">
          <button type="button" class="linkish doc-link" @click="openDoc(d.id)">
            {{ d.name || d.id.slice(0, 8) }}
          </button>
          <span class="muted">
            · {{ d.docClass || '—' }} · {{ d.status }} · {{ d.source }} ·
            {{ formatDate(d.createdAt) }}
          </span>
          <p class="muted" v-if="d.snippet">{{ d.snippet }}</p>
        </li>
        <li v-if="!documents.length" class="muted">Nenhum documento listado.</li>
      </ul>
    </div>

    <div class="panel" v-if="detail" style="margin-top: 1rem">
      <div class="actions">
        <h2>{{ detail.document.name || 'Documento' }}</h2>
        <button type="button" class="secondary" @click="detail = null">Fechar</button>
        <button type="button" @click="reprocess(detail.document.id)">Reprocessar OCR</button>
      </div>
      <p class="muted">
        {{ detail.document.mimeType }} · {{ detail.document.status }} ·
        {{ detail.document.docClass || 'sem classe' }}
      </p>
      <p class="muted" v-if="detail.document.lastError">Erro: {{ detail.document.lastError }}</p>
      <template v-if="detail.extraction">
        <h3>Campos</h3>
        <ul class="plain">
          <li v-for="f in detail.extraction.fields" :key="f.name">
            <strong>{{ f.name }}</strong>: {{ f.value ?? '—' }}
            <span class="muted">({{ f.method }}, {{ Math.round((f.confidence ?? 0) * 100) }}%)</span>
          </li>
        </ul>
        <h3>Texto extraído</h3>
        <pre class="extract">{{ detail.extraction.textPreview }}</pre>
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

type ProviderInfo = {
  id: string;
  name: string;
  status: string;
  accountLabel?: string;
  scopes?: string[];
  lastTestAt?: string;
  error?: string;
  capabilities: {
    list: boolean;
    search: boolean;
    download: boolean;
    upload: boolean;
    createFolder: boolean;
    oauth: boolean;
  };
};

type DocRow = {
  id: string;
  name?: string;
  mimeType?: string;
  status: string;
  docClass?: string;
  source?: string;
  createdAt: string;
  snippet?: string;
};

const route = useRoute();
const router = useRouter();

const providers = ref<ProviderInfo[]>([]);
const apps = ref({ google: false, microsoft: false, dropbox: false });
const busy = ref('');
const flashOk = ref('');
const flashErr = ref('');
const uploadProvider = ref('local');
const folderId = ref('');
const runOcr = ref(true);
const dragOver = ref(false);
const uploads = ref<Array<{ key: string; name: string; state: string; detail?: string }>>([]);
const documents = ref<DocRow[]>([]);
const docQuery = ref('');
const docsLoading = ref(false);
const detail = ref<{
  document: DocRow & { lastError?: string };
  extraction: {
    fields: Array<{ name: string; value?: string | null; method: string; confidence?: number }>;
    textPreview: string;
  } | null;
} | null>(null);

function statusLabel(s: string) {
  const map: Record<string, string> = {
    connected: 'conectado',
    disconnected: 'desconectado',
    error: 'erro',
    expired: 'expirado',
    unavailable: 'indisponível',
  };
  return map[s] || s;
}

function statusClass(s: string) {
  if (s === 'connected') return 'ok';
  if (s === 'error' || s === 'expired') return 'down';
  if (s === 'unavailable') return 'not_configured';
  return 'degraded';
}

function formatDate(iso?: string) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
}

function appsReady(id: string) {
  if (id === 'google_drive') return apps.value.google;
  if (id === 'onedrive') return apps.value.microsoft;
  if (id === 'dropbox') return apps.value.dropbox;
  return true;
}

async function loadProviders() {
  const res = await fetch('/api/integrations', { credentials: 'include' });
  if (!res.ok) throw new Error(`Integrações HTTP ${res.status}`);
  const data = (await res.json()) as {
    providers: ProviderInfo[];
    apps?: { google?: boolean; microsoft?: boolean; dropbox?: boolean };
  };
  providers.value = data.providers ?? [];
  apps.value = {
    google: Boolean(data.apps?.google),
    microsoft: Boolean(data.apps?.microsoft),
    dropbox: Boolean(data.apps?.dropbox),
  };
}

async function loadDocs() {
  docsLoading.value = true;
  try {
    const qs = new URLSearchParams();
    if (docQuery.value.trim()) qs.set('q', docQuery.value.trim());
    qs.set('limit', '40');
    const res = await fetch(`/api/documents?${qs}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`Docs HTTP ${res.status}`);
    const data = (await res.json()) as { documents: DocRow[] };
    documents.value = data.documents ?? [];
  } finally {
    docsLoading.value = false;
  }
}

async function connect(id: string) {
  busy.value = id;
  flashErr.value = '';
  try {
    const res = await fetch(`/api/integrations/${id}/connect`, {
      method: 'POST',
      credentials: 'include',
    });
    const data = (await res.json()) as { url?: string; detail?: string };
    if (!res.ok || !data.url) throw new Error(data.detail || 'Não foi possível iniciar OAuth');
    window.location.href = data.url;
  } catch (err) {
    flashErr.value = err instanceof Error ? err.message : 'erro';
  } finally {
    busy.value = '';
  }
}

async function disconnect(id: string) {
  busy.value = id;
  try {
    const res = await fetch(`/api/integrations/${id}/disconnect`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    flashOk.value = 'Desconectado.';
    await loadProviders();
  } catch (err) {
    flashErr.value = err instanceof Error ? err.message : 'erro';
  } finally {
    busy.value = '';
  }
}

async function test(id: string) {
  busy.value = id;
  try {
    const res = await fetch(`/api/integrations/${id}/test`, {
      method: 'POST',
      credentials: 'include',
    });
    const data = (await res.json()) as { ok?: boolean; detail?: string };
    if (data.ok) flashOk.value = `Teste OK${data.detail ? `: ${data.detail}` : ''}`;
    else flashErr.value = data.detail || 'Teste falhou';
    await loadProviders();
  } catch (err) {
    flashErr.value = err instanceof Error ? err.message : 'erro';
  } finally {
    busy.value = '';
  }
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function uploadFile(file: File) {
  const key = `${file.name}-${file.size}-${Date.now()}`;
  uploads.value = [{ key, name: file.name, state: 'enviando…' }, ...uploads.value].slice(0, 12);
  try {
    const buf = await file.arrayBuffer();
    const base64 = toBase64(new Uint8Array(buf));
    const res = await fetch(`/api/integrations/${uploadProvider.value}/upload`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        folderId: folderId.value || undefined,
        base64,
        runOcr: runOcr.value,
      }),
    });
    const data = (await res.json()) as {
      ok?: boolean;
      detail?: string;
      error?: string;
      cloud?: { name?: string; id?: string };
      document?: { id?: string; status?: string; duplicateFile?: boolean };
    };
    if (!res.ok) throw new Error(data.detail || data.error || `HTTP ${res.status}`);
    const parts = [
      data.cloud ? `nuvem ${data.cloud.name || data.cloud.id}` : null,
      data.document
        ? `doc ${data.document.id?.slice(0, 8)} (${data.document.status}${data.document.duplicateFile ? ', dup' : ''})`
        : runOcr.value
          ? 'OCR não aceitou'
          : 'sem OCR',
    ].filter(Boolean);
    const row = uploads.value.find((u) => u.key === key);
    if (row) {
      row.state = 'ok';
      row.detail = parts.join(' · ');
    }
    await loadDocs();
  } catch (err) {
    const row = uploads.value.find((u) => u.key === key);
    if (row) {
      row.state = 'erro';
      row.detail = err instanceof Error ? err.message : 'falha';
    }
  }
}

function onPick(ev: Event) {
  const input = ev.target as HTMLInputElement;
  const files = input.files ? Array.from(input.files) : [];
  input.value = '';
  for (const f of files) void uploadFile(f);
}

function onDrop(ev: DragEvent) {
  dragOver.value = false;
  const files = ev.dataTransfer?.files ? Array.from(ev.dataTransfer.files) : [];
  for (const f of files) void uploadFile(f);
}

async function openDoc(id: string) {
  const res = await fetch(`/api/documents/${id}`, { credentials: 'include' });
  if (!res.ok) {
    flashErr.value = `Documento HTTP ${res.status}`;
    return;
  }
  detail.value = (await res.json()) as typeof detail.value;
}

async function reprocess(id: string) {
  const res = await fetch(`/api/documents/${id}/reprocess`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    flashErr.value = 'Falha ao reprocessar';
    return;
  }
  flashOk.value = 'Reprocessamento enfileirado.';
  await openDoc(id);
  await loadDocs();
}

onMounted(async () => {
  const connected = typeof route.query.connected === 'string' ? route.query.connected : '';
  const error = typeof route.query.error === 'string' ? route.query.error : '';
  if (connected) flashOk.value = `Conectado: ${connected}`;
  if (error) flashErr.value = error;
  if (connected || error) {
    await router.replace({ path: '/integracoes', query: {} });
  }
  try {
    await loadProviders();
    await loadDocs();
  } catch (err) {
    flashErr.value = err instanceof Error ? err.message : 'Falha ao carregar';
  }
});
</script>

<style scoped>
.flash {
  margin: 0.75rem 0 0;
  padding: 0.55rem 0.75rem;
  border-radius: 0.65rem;
}
.flash.ok {
  background: rgba(31, 122, 77, 0.12);
  color: var(--ok);
}
.flash.err {
  background: rgba(163, 59, 45, 0.1);
  color: var(--down);
}
.err-text {
  color: var(--down);
}
button.secondary {
  background: transparent;
  color: var(--ink);
  border: 1px solid var(--line);
}
.drop {
  border: 1.5px dashed var(--line);
  border-radius: 0.85rem;
  padding: 1.25rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.45);
}
.drop.over {
  border-color: #2f6f57;
  background: rgba(47, 111, 87, 0.08);
}
.file-btn {
  display: inline-block;
  margin-top: 0.5rem;
  padding: 0.45rem 0.8rem;
  border-radius: 0.65rem;
  background: #2f6f57;
  color: #f7faf8;
  cursor: pointer;
}
.inline {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.inline input {
  border: 1px solid var(--line);
  border-radius: 0.65rem;
  padding: 0.45rem 0.65rem;
  min-width: 12rem;
}
.doc-link {
  font-weight: 600;
}
.extract {
  white-space: pre-wrap;
  font-size: 0.82rem;
  max-height: 240px;
  overflow: auto;
  background: rgba(255, 255, 255, 0.55);
  padding: 0.75rem;
  border-radius: 0.65rem;
  border: 1px solid var(--line);
}
select {
  border: 1px solid var(--line);
  border-radius: 0.65rem;
  padding: 0.55rem 0.7rem;
  background: rgba(255, 255, 255, 0.9);
  font: inherit;
}
</style>
